/**
 * When the Supporting pages card is allowed to say something.
 *
 * The card is fed by three sources, not two, and that is the whole reason this
 * file exists. Two of them are snapshots that report through
 * `topicOwnershipStatus` and `topicOwnershipV2Status`; the third is a live
 * marked-competitor evaluation that starts only *after* both of those report
 * ready and lands seconds later, adding rows to a card that has already told
 * the operator it is finished.
 *
 * Reported from the app: the card read "Covered elsewhere", then "Not covered
 * elsewhere" a few seconds later, on the same page without touching anything.
 *
 * Two surfaces want opposite behaviour here and both are right, which is why
 * `settledOnly` is an input rather than a constant:
 *
 * **Inventory** is where an operator digs. A card filling in as evidence
 * arrives is the page working in front of them, and making it wait for the
 * slowest source would stall the screen for rows most visits never read.
 *
 * **Review** is where an operator decides. A verdict that changes while it is
 * being read does not teach them two facts — it teaches them this card cannot
 * be trusted, and that doubt is not confined to the card that earned it. A card
 * that arrives late costs nothing. A card that changes its mind costs the
 * surface.
 */

export type SupportingPagesLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface SupportingPagesGateState {
  /** The by-topic snapshot. */
  topicOwnershipStatus: SupportingPagesLoadStatus;
  /** The opportunity snapshot. */
  topicOwnershipV2Status: SupportingPagesLoadStatus;
  /** The live marked-competitor evaluation — the source neither status covers. */
  markedSerpEvalPending: boolean;
  /**
   * Our own site's URLs have not landed. Only the review card reads them (a
   * rival page we already have is dropped), so only `settledOnly` waits.
   */
  ownSiteUrlsPending: boolean;
  /** How many columns the card would render right now. */
  columnCount: number;
  /** Review surfaces set this. Inventory and URL Detail do not. */
  settledOnly: boolean;
}

/** Whether anything can still change what this card says. */
export function supportingPagesSettling(state: SupportingPagesGateState): boolean {
  if (state.topicOwnershipStatus === 'loading') return true;
  if (state.topicOwnershipV2Status === 'loading') return true;
  if (state.settledOnly && state.ownSiteUrlsPending) return true;
  return state.markedSerpEvalPending;
}

/**
 * Whether the card may render its columns.
 *
 * On a review surface this waits for the live evaluation as well; everywhere
 * else it keeps the behaviour the deep-dive surfaces have always had.
 */
export function supportingPagesColumnsReady(state: SupportingPagesGateState): boolean {
  if (state.columnCount <= 0) return false;
  if (state.settledOnly) return !supportingPagesSettling(state);
  return state.topicOwnershipStatus !== 'loading' && state.topicOwnershipV2Status !== 'loading';
}

/**
 * Whether "no supporting pages found" is safe to say yet.
 *
 * The empty state is a claim like any other, and on a review surface it is the
 * one that flips most visibly: it reads as "no gap here", the live evaluation
 * lands, and a gap appears under an operator who already decided there was not
 * one. An empty state is never urgent enough to be worth showing early.
 */
export function supportingPagesEmptyReady(state: SupportingPagesGateState): boolean {
  if (state.topicOwnershipStatus !== 'ready') return false;
  if (state.settledOnly) return !supportingPagesSettling(state);
  return state.topicOwnershipV2Status !== 'loading';
}
